import React, { useState } from 'react';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  Plus, Edit2, Trash2, Network, ChevronLeft, Save, X,
  CheckCircle, XCircle, RefreshCw, Copy, Eye, EyeOff,
  Info, Settings, Hash, Phone
} from 'lucide-react';

const SERVER_IP = window.location.hostname;

const CODEC_OPTIONS = ['ulaw', 'alaw', 'g722', 'g729', 'opus', 'gsm'];

function emptyTrunk() {
  return {
    name: '', type: 'sip', host: '', port: 5060,
    username: '', password: '', fromDomain: '', fromUser: '',
    callerIdNumber: '', callerIdName: '',
    codecs: 'ulaw,alaw', dtmfMode: 'rfc4733',
    transport: 'udp', register: true,
    maxChannels: 30, description: '', status: 'active',
    allowInbound: true, allowOutbound: true,
    sendDiversion: false, allowRefer: false, department: '',
  };
}

// ── Trunk Editor ───────────────────────────────────────────────────────────────
function TrunkEditor({ trunk, extensions = [], onSave, onCancel, saving }) {
  const isEdit = !!trunk?.id;
  const [tab, setTab] = useState('general');
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState(() => trunk ? {
    name: trunk.name || '',
    type: trunk.type || 'sip',
    host: trunk.host || '',
    port: trunk.port || 5060,
    username: trunk.username || '',
    password: '',
    fromDomain: trunk.from_domain || '',
    fromUser: trunk.from_user || '',
    callerIdNumber: trunk.caller_id_number || '',
    callerIdName: trunk.caller_id_name || '',
    codecs: trunk.codecs || 'ulaw,alaw',
    dtmfMode: trunk.dtmf_mode || 'rfc4733',
    transport: trunk.transport || 'udp',
    register: trunk.register !== false,
    maxChannels: trunk.max_channels || 0,
    description: trunk.description || '',
    status: trunk.status || 'active',
    allowInbound: trunk.allow_inbound !== false,
    allowOutbound: trunk.allow_outbound !== false,
    sendDiversion: !!trunk.send_diversion,
    allowRefer: !!trunk.allow_refer,
    department: trunk.department || '',
    autoDetectPort: trunk.port === 0 || !trunk.port,
    defaultDest: trunk.default_dest || '',
  } : { ...emptyTrunk(), autoDetectPort: false });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleCodec = (c) => {
    const list = form.codecs ? form.codecs.split(',').filter(Boolean) : [];
    const next = list.includes(c) ? list.filter(x => x !== c) : [...list, c];
    set('codecs', next.join(','));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (!form.host.trim()) { toast.error('El servidor es obligatorio'); return; }
    if (!form.username.trim()) { toast.error('El usuario es obligatorio'); return; }
    if (!isEdit && !form.password.trim()) { toast.error('La contraseña es obligatoria'); return; }
    const payload = { ...form, port: form.autoDetectPort ? 0 : parseInt(form.port) || 5060 };
    if (!payload.password) delete payload.password;
    onSave(payload);
  };

  const selectedCodecs = form.codecs ? form.codecs.split(',').filter(Boolean) : [];

  const TABS = [
    { key: 'general', label: 'General', icon: Settings },
    { key: 'did', label: 'Números DID', icon: Hash },
    { key: 'options', label: 'Opciones', icon: Phone },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      {/* Top bar */}
      <div className="flex items-center gap-4 mb-6">
        <button type="button" onClick={onCancel} aria-label="Volver"
          className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors">
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-xl font-bold text-white">{isEdit ? trunk.name : 'Nueva Troncal SIP'}</h2>
        <div className="ml-auto flex gap-2">
          {isEdit && (
            <button type="button"
              onClick={() => toast('Verificando troncal SIP...', { icon: '🔍' })}
              className="btn-secondary flex items-center gap-2 text-sm">
              <RefreshCw size={14} /> Revisar Troncal SIP
            </button>
          )}
          <button type="submit" disabled={saving}
            className="btn-primary flex items-center gap-2">
            <Save size={16} /> Guardar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-gray-800 mb-6">
        {TABS.map(({ key, label }) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-muted hover:text-gray-300'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="space-y-6">
          {/* Name + default route */}
          <div className="card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label" htmlFor="trunk-name">Nombre <span className="text-red-400">*</span></label>
                <input id="trunk-name" className="form-input" value={form.name} disabled={isEdit}
                  onChange={e => set('name', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  placeholder="netelip, megacall..." />
                {isEdit && <p className="text-xs text-muted mt-1">El nombre no se puede cambiar</p>}
              </div>
              <div>
                <label className="form-label" htmlFor="trunk-status">Estado</label>
                <select id="trunk-status" className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">Activa</option>
                  <option value="inactive">Inactiva</option>
                </select>
              </div>
            </div>
            {form.description !== undefined && (
              <div className="mt-4">
                <label className="form-label" htmlFor="trunk-description">Descripción</label>
                <input id="trunk-description" className="form-input" value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Descripción opcional" />
              </div>
            )}
          </div>

          {/* Proveedor VoIP info */}
          <div className="card bg-blue-900/10 border border-blue-800/30">
            <div className="flex items-start gap-3">
              <Info size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-300 font-medium">Proveedor VoIP</p>
                <p className="text-sm text-gray-400 mt-1">
                  Para que la troncal funcione, agrega la IP de este servidor a la lista blanca de tu proveedor:
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="text-blue-300 font-mono font-bold text-sm bg-blue-900/30 px-3 py-1 rounded">
                    {SERVER_IP}
                  </code>
                  <button type="button" aria-label="Copiar IP del servidor"
                    onClick={() => { navigator.clipboard?.writeText(SERVER_IP); toast.success('IP copiada'); }}
                    className="text-muted hover:text-blue-400 transition-colors">
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Detalles de la cuenta */}
          <div className="card">
            <h3 className="text-sm font-semibold text-white mb-4">Detalles de la Cuenta</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label" htmlFor="trunk-callerid-number">Número Principal de Troncal <span className="text-red-400">*</span></label>
                <input id="trunk-callerid-number" className="form-input" value={form.callerIdNumber}
                  onChange={e => set('callerIdNumber', e.target.value)}
                  placeholder="Ej: 0869711560" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label" htmlFor="trunk-username">ID de Autenticación (Usuario SIP) <span className="text-red-400">*</span></label>
                  <input id="trunk-username" className="form-input" value={form.username}
                    onChange={e => set('username', e.target.value)}
                    placeholder="Ej: 0869711560" />
                </div>
                <div>
                  <label className="form-label" htmlFor="trunk-password">
                    Contraseña de autenticación {!isEdit && <span className="text-red-400">*</span>}
                  </label>
                  <div className="relative">
                    <input id="trunk-password" className="form-input pr-10" type={showPass ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => set('password', e.target.value)}
                      placeholder={isEdit ? '(sin cambios)' : 'Contraseña'} />
                    <button type="button" onClick={() => setShowPass(s => !s)}
                      aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-gray-300">
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Detalles del servidor */}
          <div className="card">
            <h3 className="text-sm font-semibold text-white mb-4">Detalles del Servidor</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="form-label" htmlFor="trunk-host">Registro / Servidor <span className="text-red-400">*</span></label>
                <input id="trunk-host" className="form-input" value={form.host}
                  onChange={e => set('host', e.target.value)}
                  placeholder="sip.proveedor.com" />
              </div>
              <div>
                <label className="form-label" htmlFor="trunk-port">Puerto <span className="text-red-400">*</span></label>
                <input id="trunk-port" className="form-input" type="number" value={form.autoDetectPort ? '' : form.port}
                  disabled={form.autoDetectPort}
                  onChange={e => set('port', e.target.value)}
                  placeholder="5060" />
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" className="accent-blue-600 w-4 h-4"
                    checked={form.autoDetectPort}
                    onChange={e => set('autoDetectPort', e.target.checked)} />
                  <span className="text-xs text-gray-400">Detección automática</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="form-label" htmlFor="trunk-transport">Transporte</label>
                <select id="trunk-transport" className="form-input" value={form.transport} onChange={e => set('transport', e.target.value)}>
                  <option value="udp">UDP</option>
                  <option value="tcp">TCP</option>
                  <option value="tls">TLS</option>
                </select>
              </div>
              <div>
                <label className="form-label" htmlFor="trunk-from-domain">From Domain (opcional)</label>
                <input id="trunk-from-domain" className="form-input" value={form.fromDomain}
                  onChange={e => set('fromDomain', e.target.value)}
                  placeholder="proveedor.com" />
              </div>
              <div>
                <label className="form-label" htmlFor="trunk-from-user">Identidad From / ID SIP (opcional)</label>
                <input id="trunk-from-user" className="form-input" value={form.fromUser}
                  onChange={e => set('fromUser', e.target.value)}
                  placeholder="ID SIP de la cuenta" />
                <p className="text-xs text-muted mt-1">
                  Solo si tienes varias troncales del mismo proveedor. Fija el <code>From</code> al
                  ID SIP de la cuenta; el caller ID personalizado se envía en PAI/RPID.
                </p>
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="accent-blue-600 w-4 h-4"
                  checked={form.register}
                  onChange={e => set('register', e.target.checked)} />
                <span className="text-sm text-gray-300">Registrar troncal en el servidor</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {tab === 'did' && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <Hash size={18} className="text-muted" />
            <h3 className="text-sm font-semibold text-white">Números DID</h3>
          </div>
          <p className="text-sm text-muted mb-6">
            Los números DID (Direct Inward Dialing) de esta troncal se configuran en
            <strong className="text-gray-300"> Plan de Marcado → Rutas Entrantes</strong>.
          </p>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
            <p className="text-xs text-muted text-center">
              Asocia DIDs a esta troncal desde la sección de Rutas Entrantes
            </p>
          </div>
        </div>
      )}

      {tab === 'options' && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-sm font-semibold text-white mb-4">Codecs y Audio</h3>
            <div>
              {/* span, no label: no es el nombre de UN control, es el encabezado de un grupo de botones toggle */}
              <span className="form-label">Codecs permitidos</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {CODEC_OPTIONS.map(c => (
                  <button key={c} type="button"
                    onClick={() => toggleCodec(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      selectedCodecs.includes(c)
                        ? 'bg-blue-600/20 border-blue-600 text-blue-300'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}>
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted mt-2">Seleccionados: {selectedCodecs.join(', ') || 'ninguno'}</p>
            </div>
          </div>

          <div className="card">
            <h3 className="text-sm font-semibold text-white mb-4">Señalización</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="trunk-dtmf-mode">Modo DTMF</label>
                <select id="trunk-dtmf-mode" className="form-input" value={form.dtmfMode} onChange={e => set('dtmfMode', e.target.value)}>
                  <option value="rfc4733">RFC 4733 (recomendado)</option>
                  <option value="inband">In-band</option>
                  <option value="info">SIP INFO</option>
                  <option value="auto">Auto</option>
                </select>
              </div>
              <div>
                <label className="form-label" htmlFor="trunk-max-channels">Canales máximos simultáneos</label>
                <input id="trunk-max-channels" className="form-input" type="number" min="0" value={form.maxChannels}
                  onChange={e => set('maxChannels', parseInt(e.target.value) || 0)}
                  placeholder="0 = ilimitado" />
                <p className="text-xs text-muted mt-1">0 = sin límite</p>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-sm font-semibold text-white mb-4">Configuración</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="trunk-department">Limitar a</label>
                <input id="trunk-department" className="form-input" value={form.department}
                  onChange={e => set('department', e.target.value)}
                  placeholder="Departamento (opcional)" />
              </div>
              <div>
                <label className="form-label" htmlFor="trunk-max-channels-2">Número de llamadas simultáneas por troncal *</label>
                <input id="trunk-max-channels-2" className="form-input" type="number" min="0" value={form.maxChannels}
                  onChange={e => set('maxChannels', parseInt(e.target.value) || 0)}
                  placeholder="30" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" className="accent-blue-600 w-4 h-4"
                  checked={form.allowInbound}
                  onChange={e => set('allowInbound', e.target.checked)} />
                Permitir llamadas entrantes
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" className="accent-blue-600 w-4 h-4"
                  checked={form.allowOutbound}
                  onChange={e => set('allowOutbound', e.target.checked)} />
                Permitir llamadas salientes
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" className="accent-blue-600 w-4 h-4"
                  checked={form.sendDiversion}
                  onChange={e => set('sendDiversion', e.target.checked)} />
                Incluir soporte de cabecera de desvío
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300" title="Permite que el proveedor/operador inicie transferencias con SIP REFER dentro de la llamada.">
                <input type="checkbox" className="accent-blue-600 w-4 h-4"
                  checked={form.allowRefer}
                  onChange={e => set('allowRefer', e.target.checked)} />
                Permitir transferencias por parte del operador (SIP REFER)
              </label>
            </div>
          </div>

          <div className="card">
            <h3 className="text-sm font-semibold text-white mb-4">Identificador de Llamada Saliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="trunk-outbound-callerid-number">Número</label>
                <input id="trunk-outbound-callerid-number" className="form-input" value={form.callerIdNumber}
                  onChange={e => set('callerIdNumber', e.target.value)}
                  placeholder="+34912345678" />
              </div>
              <div>
                <label className="form-label" htmlFor="trunk-callerid-name">Nombre</label>
                <input id="trunk-callerid-name" className="form-input" value={form.callerIdName}
                  onChange={e => set('callerIdName', e.target.value)}
                  placeholder="Empresa S.L." />
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

// ── Status badge ───────────────────────────────────────────────────────────────
// Muestra el estado de REGISTRO EN VIVO (reg_status del backend): si la troncal
// no está registrada con el proveedor, aparece "Caída" en rojo. Si está
// deshabilitada en la app (status != active), tiene prioridad "Inactiva".
function RegStatus({ trunk }) {
  const reg = trunk.reg_status;
  let cls, Icon, label;
  if (trunk.status !== 'active') {
    cls = 'text-gray-400 border-gray-700'; Icon = XCircle; label = 'Inactiva';
  } else if (reg === 'Registered') {
    cls = 'badge-success border-green-800'; Icon = CheckCircle; label = 'Registrada';
  } else if (reg === 'No aplica') {
    cls = 'text-gray-400 border-gray-700'; Icon = CheckCircle; label = 'Sin registro';
  } else if (reg === undefined) {
    cls = 'text-gray-400 border-gray-700'; Icon = CheckCircle; label = 'Activa';
  } else if (reg === 'Sin datos') {
    // El backend no pudo consultar a Asterisk: no es lo mismo que "caída".
    cls = 'text-gray-400 border-gray-700'; Icon = XCircle; label = 'Sin datos';
  } else {
    // Rejected / Unregistered / Rejected(exp) → CAÍDA
    cls = 'badge-danger border-red-800'; Icon = XCircle; label = 'Caída';
  }
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded border ${cls}`}
      title={reg === 'Sin datos' ? 'No se pudo consultar a Asterisk' : reg ? `Registro: ${reg}` : undefined}>
      <Icon size={11} />
      {label}
    </span>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function TrunksPage() {
  const qc = useQueryClient();
  const [editTrunk, setEditTrunk] = useState(null); // null=list, false=new, object=edit
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  useEscapeKey(!!deleteConfirm, () => setDeleteConfirm(null));

  // refetchInterval: el estado de registro (Caída/Registrada) se actualiza solo.
  const { data: trunks = [], isLoading } = useQuery('trunks',
    () => api.get('/trunks').then(r => r.data), { refetchInterval: 15000 });
  const { data: extensions = [] } = useQuery('extensions-list',
    () => api.get('/extensions').then(r => r.data), { staleTime: 60000 });

  const saveMut = useMutation(
    (d) => editTrunk?.id
      ? api.put('/trunks/' + editTrunk.id, d).then(r => r.data)
      : api.post('/trunks', d).then(r => r.data),
    {
      // Feedback inmediato: "Guardando…" que se resuelve en "Guardado".
      onMutate: () => toast.loading('Guardando troncal…', { id: 'trunk-save' }),
      onSuccess: () => {
        qc.invalidateQueries('trunks');
        toast.success(editTrunk?.id ? 'Troncal guardada' : 'Troncal creada', { id: 'trunk-save' });
        setEditTrunk(null);
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Error al guardar', { id: 'trunk-save' }),
    }
  );

  const delMut = useMutation(
    (id) => api.delete('/trunks/' + id).then(r => r.data),
    {
      onSuccess: () => {
        qc.invalidateQueries('trunks');
        toast.success('Troncal eliminada');
        setDeleteConfirm(null);
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Error al eliminar'),
    }
  );

  if (editTrunk !== null) {
    return (
      <div className="space-y-6">
        <TrunkEditor
          trunk={editTrunk || null}
          extensions={extensions}
          onSave={(d) => saveMut.mutate(d)}
          onCancel={() => setEditTrunk(null)}
          saving={saveMut.isLoading}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Troncales SIP</h1>
          <p className="text-gray-400 text-sm">{trunks.length} troncal{trunks.length !== 1 ? 'es' : ''} configurada{trunks.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setEditTrunk(false)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Nueva Troncal
        </button>
      </div>

      {isLoading ? (
        <div className="card text-center py-12"><div className="spinner mx-auto" /></div>
      ) : !trunks.length ? (
        <div className="card text-center py-16">
          <Network size={40} className="text-gray-700 mx-auto mb-3" />
          <p className="text-muted font-medium">Sin troncales configuradas</p>
          <p className="text-muted text-sm mt-1">Agrega una troncal SIP para conectar con la PSTN</p>
          <button onClick={() => setEditTrunk(false)} className="btn-primary mt-4 inline-flex items-center gap-2">
            <Plus size={16} /> Agregar Troncal
          </button>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                {['Nombre', 'Servidor', 'Usuario', 'Codecs', 'Transporte', 'Estado', ''].map(h => (
                  <th key={h} className="table-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trunks.map((t) => (
                <tr key={t.id} className="table-row border-b border-gray-800/50">
                  <td className="table-td">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-600/15 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Network size={15} className="text-blue-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-100">{t.name}</div>
                        {t.description && <div className="text-xs text-muted">{t.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="table-td">
                    <div className="font-mono text-sm text-gray-300">{t.host}</div>
                    <div className="text-xs text-muted">:{t.port || 'auto'}</div>
                  </td>
                  <td className="table-td text-sm text-gray-400">{t.username}</td>
                  <td className="table-td">
                    <div className="flex flex-wrap gap-1">
                      {(t.codecs || '').split(',').filter(Boolean).map(c => (
                        <span key={c} className="text-xs px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded">
                          {c.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="table-td text-xs text-muted uppercase">{t.transport || 'UDP'}</td>
                  <td className="table-td"><RegStatus trunk={t} /></td>
                  <td className="table-td text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditTrunk(t)} aria-label={`Editar troncal ${t.name}`}
                        className="p-1.5 text-muted hover:text-blue-400 hover:bg-blue-900/20 rounded transition-colors">
                        <Edit2 size={15} />
                      </button>
                      <button onClick={() => setDeleteConfirm(t)} aria-label={`Eliminar troncal ${t.name}`}
                        className="p-1.5 text-muted hover:text-red-400 hover:bg-red-900/20 rounded transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-modal="true" className="card max-w-md w-full space-y-4">
            <h3 className="text-lg font-semibold text-white">Eliminar troncal</h3>
            <p className="text-gray-400">
              ¿Eliminar <span className="text-white font-medium">"{deleteConfirm.name}"</span>?
              Se eliminará la configuración PJSIP y el registro SIP. Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancelar</button>
              <button onClick={() => delMut.mutate(deleteConfirm.id)} disabled={delMut.isLoading}
                className="btn-danger flex items-center gap-2">
                <Trash2 size={15} /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
